import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Search, Loader2, Cpu, ExternalLink, MapPin,
    Building2, Filter, CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5022';

const DEVICE_TYPE_CONFIG = {
    'Аксиома-01': { icon: '🔵', badge: 'bg-green-500/10 text-green-300 border-green-500/20', route: 'aksioma01' },
    'Аксиома-03': { icon: '🟣', badge: 'bg-blue-500/10 text-blue-300 border-blue-500/20', route: 'aksioma03' },
    'Карат-307': { icon: '🟢', badge: 'bg-amber-500/10 text-amber-300 border-amber-500/20', route: 'karat307' },
};

const formatAddress = (device) => {
    const parts = [];
    if (device.city) parts.push(device.city);
    if (device.street) parts.push(device.street);
    if (device.houseNumber) parts.push(`д. ${device.houseNumber}`);
    return parts.length > 0 ? parts.join(', ') : 'Адрес не указан';
};

const DeviceSelector = ({ selectedDevice, onSelect }) => {
    const navigate = useNavigate();
    const [devices, setDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        loadDevices();
    }, []);

    const loadDevices = async () => {
        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(`${API_URL}/api/devices`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const devicesList = Array.isArray(response.data)
                ? response.data
                : (response.data?.onServiceDevices || []);

            // Сортировка по адресу
            const sorted = [...devicesList].sort((a, b) =>
                formatAddress(a).localeCompare(formatAddress(b), 'ru-RU')
            );

            setDevices(sorted);
        } catch (err) {
            console.error('Ошибка загрузки устройств:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredDevices = useMemo(() => {
        return devices.filter(d => {
            const address = formatAddress(d).toLowerCase();
            const matchesSearch = !searchQuery ||
                address.includes(searchQuery.toLowerCase()) ||
                d.name?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesType = typeFilter === 'all' || d.deviceType === typeFilter;
            return matchesSearch && matchesType;
        });
    }, [devices, searchQuery, typeFilter]);

    const handleOpenDevice = (e, device) => {
        e.stopPropagation();
        // Переход на страницу устройства
        navigate(`/devices?type=${DEVICE_TYPE_CONFIG[device.deviceType]?.route || ''}&name=${encodeURIComponent(device.name)}`);
    };

    return (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            {/* Заголовок + фильтры */}
            <div className="p-4 border-b border-slate-700">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <Cpu className="w-5 h-5 text-cyan-400" />
                        Выбор устройства
                        {selectedDevice && (
                            <span className="ml-2 px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded text-xs border border-cyan-500/30">
                                {formatAddress(selectedDevice)}
                            </span>
                        )}
                    </h3>

                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Поиск по адресу или имени..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full sm:w-64 pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                        </div>
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="w-full sm:w-44 pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none"
                            >
                                <option value="all">Все типы</option>
                                <option value="Аксиома-01">Аксиома-01</option>
                                <option value="Аксиома-03">Аксиома-03</option>
                                <option value="Карат-307">Карат-307</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Список устройств */}
            <div className="max-h-72 overflow-y-auto custom-scrollbar">
                {isLoading ? (
                    <div className="flex items-center justify-center py-10 text-slate-400">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        Загрузка устройств...
                    </div>
                ) : filteredDevices.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-slate-500">
                        <Building2 className="w-10 h-10 mb-2" />
                        <p className="text-sm">Устройства не найдены</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-700/50">
                        {filteredDevices.map(device => {
                            const isSelected = selectedDevice?.name === device.name;
                            const typeConfig = DEVICE_TYPE_CONFIG[device.deviceType] || {};
                            const address = formatAddress(device);

                            return (
                                <div
                                    key={device.name}
                                    onClick={() => onSelect(device)}
                                    className={`group flex items-center gap-3 px-4 py-3 cursor-pointer transition ${isSelected
                                            ? 'bg-cyan-600/15 border-l-3 border-l-cyan-400'
                                            : 'hover:bg-slate-700/50 border-l-3 border-l-transparent'
                                        }`}
                                >
                                    {/* Индикатор выбора */}
                                    <div className="flex-shrink-0">
                                        {isSelected ? (
                                            <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                                        ) : (
                                            <div className="w-5 h-5 rounded-full border-2 border-slate-600 group-hover:border-slate-400 transition" />
                                        )}
                                    </div>

                                    {/* Иконка типа */}
                                    <span className="text-lg flex-shrink-0">{typeConfig.icon || '⚪'}</span>

                                    {/* Основная информация: адрес первым */}
                                    <div className="flex-1 min-w-0">
                                        <div className={`font-medium text-sm truncate ${isSelected ? 'text-cyan-200' : 'text-white'}`}>
                                            <MapPin className="w-3.5 h-3.5 inline-block mr-1.5 text-slate-400" />
                                            {address}
                                        </div>
                                        <div className="text-xs text-slate-500 truncate mt-0.5">
                                            {device.name}
                                        </div>
                                    </div>

                                    {/* Бейдж типа */}
                                    <span className={`hidden sm:inline-flex text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${typeConfig.badge || 'bg-slate-500/10 text-slate-300 border-slate-500/20'}`}>
                                        {device.deviceType}
                                    </span>

                                    {/* Ссылка на устройство */}
                                    <button
                                        onClick={(e) => handleOpenDevice(e, device)}
                                        className="flex-shrink-0 p-2 rounded-lg text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition opacity-0 group-hover:opacity-100"
                                        title="Открыть карточку устройства"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Футер с количеством */}
            {!isLoading && (
                <div className="px-4 py-2 bg-slate-700/30 border-t border-slate-700 flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                        Показано: {filteredDevices.length} из {devices.length}
                    </span>
                    {selectedDevice && (
                        <span className="text-xs text-cyan-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Выбрано: {formatAddress(selectedDevice)}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default DeviceSelector;